/**
 * Wraps an action so it runs only when under usage limit. If over limit, triggers the Usage Limit
 * Upgrade Dialog and skips the action. Side effects: toasts "Checking usage limits..."
 * when loading; invokes PlanUpgradeDialog via usePlanUsageGuard callback when over limit.
 * Architectural Context: Feature Guard. Use for create-job, send-invite, analyze-cv, etc.
 *
 * @param metric - Usage bucket to check: 'cv' (CV Analysis) or 'interview'
 * @param quantity - Units required; use a getter function for dynamic values (e.g. file count)
 * @param origin - GTM origin string for tracking where the guard was triggered
 * @default quantity 1
 * @returns guardedAction - Wraps a function; returns same signature, runs only when under limit
 * @returns isChecking - True while plan data is loading
 *
 * @example
 * const { guardedAction } = useGuardedAction('cv', 1, 'add_cv_button');
 * const handleSubmit = guardedAction(() => submitForm());
 *
 * @example
 * const { guardedAction } = useGuardedAction('cv', () => files.length, 'upload_modal');
 * guardedAction(() => uploadFiles())();
 */

import { useCallback, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import i18n from '@/i18n';
import { usePlanUsageGuard, UsageMetric } from './usePlanUsageGuard';
export const useGuardedAction = (
  metric: UsageMetric,
  quantity: number | (() => number) = 1,
  origin?: string
) => {
  const { checkUsageLimit, isLoading } = usePlanUsageGuard();
  
  const quantityRef = useRef(quantity);
  useEffect(() => {
    quantityRef.current = quantity;
  }, [quantity]);

  const guardedAction = useCallback(
    <T extends (...args: any[]) => any>(action: T): T => {
      return ((...args: Parameters<T>) => {
        if (isLoading) {
          toast.info(i18n.t('common:toasts.checking_usage_limits'));
          return;
        }
        const resolvedQuantity = typeof quantityRef.current === 'function' ? quantityRef.current() : quantityRef.current;
        const limitCheck = checkUsageLimit({ metric, quantity: resolvedQuantity, origin });
        if (limitCheck === null) {
          toast.info(i18n.t('common:toasts.checking_usage_limits'));
          return;
        }
        if (!limitCheck.allowed) return;
        return action(...args);
      }) as T;
    },
    [checkUsageLimit, isLoading, metric, origin]
  );

  return {
    guardedAction,
    isChecking: isLoading,
  };
};
